import React, { useState } from "react";
import { Question, Sparkle, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { ai } from "../../lib/api";
import { projectCoachSnapshot } from "../../lib/gamecraft";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const STEPS = [
  { t: "1. Idea de 1 frase", b: "Qué hace el jugador cada 20 segundos. Ej: «saltar, golpear slime, recoger gema»." },
  { t: "2. Cámara y controles", b: "Lado (plataformas) o arriba (habitación). WASD + salto o ataque. Pruébalo ya en Motor." },
  { t: "3. Un héroe", b: "En Ideas → Personajes: HP, daño, sprite walk. Sin héroe el playtest no arranca bien." },
  { t: "4. Un enemigo y un arma", b: "Un mob con telegráfo claro + un arma visible. Nada de catálogo enorme al inicio." },
  { t: "5. Audio mínimo", b: "Tres SFX: salto, golpe, pickup. El oído vende más que 40 sprites." },
  { t: "6. Playtest y notas", b: "Pulsa Run. Anota lo que se siente mal. Corta scope. Repite." },
];

const TIPS = [
  "Silueta legible a 32px. Si no se lee en el motor, no sirve el detalle.",
  "Un loop corto vence a un mundo enorme vacío.",
  "Congela el scope cuando tengas héroe + 1 enemigo + 1 arma jugables.",
  "La IA sugiere; tú apruebas. No dejes que genere 30 enemigos.",
  "Pon tu propia API (Gemini, Claude o ChatGPT) en Ajustes si quieres más cuota.",
];

export function HelpButton({ onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={"gc-btn gc-btn-ghost h-10 w-10 p-0 " + className}
      title="Ayuda: tu primer juego"
      aria-label="Ayuda"
    >
      <Question size={18} weight="bold" />
    </button>
  );
}

export default function HelpGuide({ open, onOpenChange, project, onJump }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("guide");
  const [q, setQ] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const missing = project ? projectCoachSnapshot(project).missing : {};

  const ask = async (question) => {
    setBusy(true);
    setOut("");
    try {
      const res = await ai.coach({
        question: question || q,
        language: "es",
        provider: user?.ai_provider || "auto",
        snapshot: project ? projectCoachSnapshot(project) : {},
      });
      setOut(res.text || "");
      setTab("ai");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo consultar la IA");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-auto" style={{ background: "var(--gc-sidebar)" }}>
        <SheetTitle>Cómo crear tu primer juego</SheetTitle>
        <div className="flex gap-1 mt-3 gc-hscroll">
          {[
            { id: "guide", label: "Guía" },
            { id: "need", label: "Qué falta" },
            { id: "keys", label: "Atajos" },
            { id: "ai", label: "IA del proyecto" },
          ].map((x) => (
            <button key={x.id} type="button" className={"gc-chip " + (tab === x.id ? "gc-chip-on" : "")} onClick={() => setTab(x.id)}>{x.label}</button>
          ))}
        </div>

        {tab === "guide" && (
          <div className="mt-4 space-y-3">
            {STEPS.map((s) => (
              <div key={s.t} className="gc-card p-3">
                <div className="font-semibold text-sm">{s.t}</div>
                <p className="text-xs mt-1" style={{ color: "var(--gc-muted)" }}>{s.b}</p>
              </div>
            ))}
            <div className="text-xs font-semibold mt-4" style={{ color: "var(--gc-muted)" }}>Consejos habituales</div>
            <ul className="text-sm space-y-1.5" style={{ color: "var(--gc-muted)" }}>
              {TIPS.map((t) => <li key={t}>• {t}</li>)}
            </ul>
            {onJump && (
              <button type="button" className="gc-btn gc-btn-primary h-11 w-full mt-2" onClick={() => { onJump("ideas"); onOpenChange(false); }}>
                Empezar en Ideas
              </button>
            )}
          </div>
        )}

        {tab === "need" && (
          <div className="mt-4 space-y-2">
            <p className="text-sm" style={{ color: "var(--gc-muted)" }}>Lectura rápida de este proyecto (sin enviar sprites).</p>
            {[
              ["Héroe / personaje", missing.hero, "ideas"],
              ["Enemigo", missing.enemy, "ideas"],
              ["Arma", missing.weapon, "ideas"],
              ["Sprites", missing.sprites, "sprites"],
              ["Audio", missing.audio, "audio"],
              ["Playtest anotado", missing.playtest, "engine"],
            ].map(([label, miss, tabKey]) => (
              <button
                key={label}
                type="button"
                className="gc-card p-3 w-full flex items-center gap-2 text-left"
                onClick={() => { if (onJump) { onJump(tabKey); onOpenChange(false); } }}
              >
                <CheckCircle size={18} className={miss ? "text-amber-500" : "text-emerald-500"} weight={miss ? "regular" : "fill"} />
                <span className="text-sm flex-1">{label}</span>
                <span className="text-xs" style={{ color: "var(--gc-muted)" }}>{miss ? "Falta" : "Ok"}</span>
              </button>
            ))}
          </div>
        )}

        {tab === "keys" && (
          <div className="mt-4 space-y-2">
            {[
              ["Ctrl/⌘ K", "Paleta de comandos"],
              ["Ctrl/⌘ Z", "Deshacer cambio estructural (fuera de inputs)"],
              ["Ctrl/⌘ Y", "Rehacer"],
              ["Alt 1–9", "Saltar de Inicio a Motor"],
              ["Alt 0", "Notas"],
              ["?", "Esta ayuda"],
              ["Run", "Playtest en el iframe Phaser"],
            ].map(([k, v]) => (
              <div key={k} className="gc-card p-3 flex items-center gap-3">
                <kbd className="text-[11px] px-2 py-1 rounded bg-black/10 font-mono shrink-0">{k}</kbd>
                <span className="text-sm">{v}</span>
              </div>
            ))}
            <p className="text-xs pt-2" style={{ color: "var(--gc-muted)" }}>
              En el editor Monaco, Ctrl+Z es el undo del código, no el del proyecto.
            </p>
          </div>
        )}

        {tab === "ai" && (
          <div className="mt-4 space-y-3">
            <p className="text-xs" style={{ color: "var(--gc-muted)" }}>
              La IA lee el resumen del proyecto (módulos, entidades, huecos). Usa la clave que tengas en Ajustes (Gemini, Claude o ChatGPT) o la del servidor.
            </p>
            <textarea
              className="gc-input w-full p-3 text-sm min-h-[80px]"
              placeholder="Ej: ¿qué recorto para un jam de 48 h?"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="flex flex-wrap gap-1.5">
              {["Siguiente paso concreto", "Qué recortar del scope", "Balance héroe vs slime", "Checklist de primer nivel"].map((c) => (
                <button key={c} type="button" className="gc-chip" disabled={busy} onClick={() => ask(c)}>{c}</button>
              ))}
            </div>
            <button type="button" className="gc-btn gc-btn-primary h-11 w-full" disabled={busy} onClick={() => ask()}>
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <><Sparkle size={16} /> Preguntar a la IA</>}
            </button>
            {out && <div className="gc-card p-3 text-sm whitespace-pre-wrap">{out}</div>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
